import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { sanitizeError } from '@shared/helpers/sanitize-error-helper';
import { JointGiftingApprovalPayload } from '@shared/models';
import { LoggerCodes } from '@shared/modules/logger/logger';
import { JointGiftingApprovalService } from '@shared/services';
import {
  JointGiftingApproval,
  JointGiftingApprovalCompleted,
  JointGiftingApprovalFailed,
  JointGiftingApprovalInProgress,
} from '@store/actions/gifting/gifting.actions';
import { JointGiftingApprovalStatus } from '@store/reducers/gifting/gifting.reducer';
import { selectClientAccountsEntity } from '@store/selectors/client-accounts/client-accounts.selectors';
import {
  selectGiftId,
  selectJointGiftingApprovalStatus,
} from '@store/selectors/gifting/gifting.selectors';
import { getJointGiftingApprovalPayload } from '@store/selectors/joint-gifting-approval/joint-gifting-approval.selectors';
import { catchError, filter, switchMap, tap, withLatestFrom } from 'rxjs';

import { Go, LogError, Next, TrackProcessError } from '../../actions';
import * as fromRootStore from '../../index';

@Injectable()
export class JointGiftingApprovalEffects {
  constructor(
    private actions$: Actions,
    private jointGiftingApprovalService: JointGiftingApprovalService,
    private store$: Store<fromRootStore.State>,
  ) {}

  jointGiftingApproval$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JointGiftingApproval),
      withLatestFrom(
        this.store$.pipe(select(selectJointGiftingApprovalStatus)),
        this.store$.pipe(select(getJointGiftingApprovalPayload)),
        this.store$.pipe(select(selectClientAccountsEntity)),
        this.store$.pipe(select(selectGiftId)),
      ),
      filter(
        ([, jointGiftingApprovalStatus]) =>
          jointGiftingApprovalStatus !== JointGiftingApprovalStatus.PENDING,
      ),
      tap(() => {
        this.store$.dispatch(JointGiftingApprovalInProgress());
      }),
      switchMap(([, , jointGiftingApprovalPayload, clientAccountsEntity, giftId]) => {
        return giftId
          ? this.additionalSigner(giftId)
          : this.initiator(jointGiftingApprovalPayload, clientAccountsEntity.vgClientPoid);
      }),
    ),
  );

  private initiator(jointGiftingApprovalPayload: JointGiftingApprovalPayload, poid: string) {
    return this.jointGiftingApprovalService.sendGift(jointGiftingApprovalPayload, poid).pipe(
      switchMap(() => [JointGiftingApprovalCompleted()]),
      catchError((e) => {
        return [
          JointGiftingApprovalFailed(),
          LogError({
            payload: {
              eventId: 'error-page-catch-jointGiftingApprovalService-sendGift',
              message: `Joint Gifting Approval Service Error: ${JSON.stringify(sanitizeError(e))}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        ];
      }),
    );
  }

  private additionalSigner(giftId: string) {
    return this.jointGiftingApprovalService.addOwnerConsentForGift(giftId).pipe(
      switchMap(() => [JointGiftingApprovalCompleted()]),
      catchError((e) => {
        return [
          JointGiftingApprovalFailed(),
          LogError({
            payload: {
              eventId: 'error-page-catch-jointGiftingApprovalService-addOwnerConsentForGift',
              message: `Joint Gifting Approval Service Error: ${JSON.stringify(sanitizeError(e))}`,
              code: LoggerCodes.NULL_FAILURE,
            },
          }),
        ];
      }),
    );
  }

  jointGiftingApprovalCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JointGiftingApprovalCompleted),
      switchMap(() => [Next()]),
    ),
  );

  private initiatorError() {
    return [
      Go({ path: ['error'] }),
      LogError({
        payload: {
          eventId: 'error-page-route-joint-gifting-approval-initiator',
          message: 'Failed to initiate Joint Gifting Approval send gift',
          code: LoggerCodes.NULL_FAILURE,
        },
      }),
      TrackProcessError({
        errorCode: 'coo_joint_gifting_approval_error-initiator',
      }),
    ];
  }

  private additionalSignerError() {
    return [
      Go({ path: ['error'] }),
      LogError({
        payload: {
          eventId: 'error-page-route-joint-gifting-approval-additional-signer',
          message: 'Failed to initiate Joint Gifting Approval additional signing',
          code: LoggerCodes.NULL_FAILURE,
        },
      }),
      TrackProcessError({
        errorCode: 'coo_joint_gifting_approval_error-additional-signer',
      }),
    ];
  }

  jointGiftingApprovalFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JointGiftingApprovalFailed),
      withLatestFrom(this.store$.pipe(select(selectGiftId))),
      switchMap(([, giftId]) => {
        return giftId ? this.initiatorError() : this.additionalSignerError();
      }),
    ),
  );
}
